// SaveThisSpotSheet — iter117.
//
// Bottom sheet that opens when the user taps the "Save This Spot" CTA
// on the Journey page (and the "+ Save Location" tile on the Connect
// page). The sheet:
//
//   1. Asks the user to confirm they are at one of the nearby
//      businesses (closest first, sorted by distance, ≤ 50 ft → 150 ft
//      → 500 ft auto-expansion).
//   2. Lets them tap a single heart to save / unsave that business.
//   3. Falls back to a custom-named spot saved with the current GPS
//      fix, written either to `/api/custom-locations` (authed) or to
//      `localStorage.habano.custom_locations` via guestStore (guest).
//
// Caller responsibility: own the GPS prompt + open the sheet only once
// coords are in hand. The sheet renders the pulsing-dot "Using your
// location now" stamp so the user knows we're privacy-aware.
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, MapPinPlus, Heart, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";
import { useI18n } from "../lib/i18n";
import * as guestStore from "../lib/guestStorage";

const RADIUS_TIERS_M = [15.24, 45.72, 152.4];        // 50 ft, 150 ft, 500 ft
const RADIUS_LABELS_FT = [50, 150, 500];

export default function SaveThisSpotSheet({ open, onClose, coords, onSaved }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  const [tierIdx, setTierIdx] = useState(0);
  const [nearby, setNearby] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedIds, setSavedIds] = useState(new Set());
  const [customName, setCustomName] = useState("");
  const [busy, setBusy] = useState(false);
  const dirty = useRef(false);

  // Reset state every time the sheet opens.
  useEffect(() => {
    if (!open) return;
    setTierIdx(0);
    setNearby([]);
    setLoading(true);
    setCustomName("");
    dirty.current = false;

    // Seed savedIds from the canonical source so initial heart state
    // reflects what the user has already favourited.
    (async () => {
      const me = await getMe();
      if (me) {
        try {
          const { data } = await api.get("/favorites");
          setSavedIds(new Set((data || []).filter((f) => f.target_type === "business").map((f) => f.target_id)));
        } catch { setSavedIds(new Set()); }
      } else {
        setSavedIds(new Set(guestStore.getSaved().filter((s) => s.target_type === "business").map((s) => s.target_id)));
      }
    })();
  }, [open]);

  // Body-scroll lock while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Fetch nearby businesses for the active radius tier. Auto-expand
  // when fewer than 3 results — but we only auto-expand once per open.
  useEffect(() => {
    if (!open || !coords) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/nearby/businesses", {
          params: { lat: coords.lat, lng: coords.lng, radius: RADIUS_TIERS_M[tierIdx], limit: 20 },
        });
        if (cancelled) return;
        const rows = data || [];
        if (rows.length < 3 && tierIdx < RADIUS_TIERS_M.length - 1) {
          setTierIdx((i) => i + 1);
          return;
        }
        setNearby(rows);
      } catch { if (!cancelled) setNearby([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, coords, tierIdx]);

  const toggleSave = async (bid) => {
    const isCurrentlySaved = savedIds.has(bid);
    // Optimistic update.
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (isCurrentlySaved) next.delete(bid); else next.add(bid);
      return next;
    });
    dirty.current = true;
    try {
      const me = await getMe();
      if (me) await api.post("/favorites/toggle", { target_id: bid, target_type: "business" });
      else guestStore.toggleSaved(bid, "business");
    } catch (e) {
      // Roll back on error.
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (isCurrentlySaved) next.add(bid); else next.delete(bid);
        return next;
      });
      toast.error(e?.response?.data?.detail || e?.message || "Could not save");
    }
  };

  const saveCustom = async () => {
    const name = customName.trim();
    if (!name) { toast.error(lang === "es" ? "Escribe un nombre" : "Enter a name"); return; }
    if (busy) return;
    setBusy(true);
    const body = {
      name,
      lat: coords.lat,
      lng: coords.lng,
      photos: [],
    };
    try {
      const me = await getMe();
      let createdId;
      let createdRow;
      if (me) {
        // iter184 — Save to BOTH API and localStorage so the map, which
        // now reads from localStorage first for zero-latency updates,
        // can render the pin immediately without a round-trip.
        const { data } = await api.post("/custom-locations", body);
        createdId = data?.id;
        createdRow = data;
        try { guestStore.upsertCustomLocation({ ...data, _origin: "owned" }); } catch { /* localStorage disabled */ }
      } else {
        const row = guestStore.addCustomLocation(body);
        createdId = row?.id;
        createdRow = row;
      }
      // iter184 — Broadcast so any live Map/Sheet component listening
      // can invalidate cache and re-hydrate without a page reload.
      try {
        window.dispatchEvent(new CustomEvent("habano:custom-locations-changed", {
          detail: { action: "create", row: createdRow },
        }));
      } catch { /* CustomEvent unsupported */ }
      toast.success(lang === "es" ? "¡Lugar guardado!" : "Spot saved!");
      onSaved?.();
      onClose?.();
      if (createdId) navigate(`/my-places/custom/${createdId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const showNearbySection = loading || nearby.length > 0;
  const radiusLabel = (lang === "es" ? "Mostrando lugares en " : "Showing businesses within ") + RADIUS_LABELS_FT[tierIdx] + (lang === "es" ? " pies" : " feet");

  return createPortal(
    <div
      data-testid="save-spot-sheet"
      role="dialog"
      aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 220, display: "flex", alignItems: "flex-end" }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        data-testid="save-spot-scrim"
        style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)", border: "none", padding: 0 }}
      />
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 480,
          margin: "0 auto",
          background: "#171717",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          borderTop: "1px solid rgba(232,184,75,0.3)",
          padding: "10px 20px 24px",
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <div aria-hidden="true" style={{ width: 40, height: 4, background: "rgba(232,184,75,0.45)", borderRadius: 9999, margin: "4px auto 8px" }} />
        <button
          type="button" onClick={onClose} data-testid="save-spot-close" aria-label="Close"
          className="absolute grid place-items-center hover:opacity-80 transition-opacity"
          style={{ top: 6, right: 6, width: 44, height: 44, minWidth: 44, minHeight: 44, background: "transparent", border: "none", padding: 0 }}
        >
          <span aria-hidden="true" className="grid place-items-center"
            style={{ width: 32, height: 32, borderRadius: 9999, background: "rgba(20,20,20,0.8)", border: "0.5px solid #333", color: "#fff" }}>
            <X style={{ width: 16, height: 16 }} strokeWidth={1.5} />
          </span>
        </button>

        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 19, color: "#F5F1E8", lineHeight: 1.25, marginRight: 48 }}>
          {lang === "es" ? "Guardar Esta Ubicación" : "Save This Location"}
        </h2>
        <div className="flex items-center gap-1.5 mt-1" data-testid="save-spot-gps-stamp">
          <PulsingDot />
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: "#9A958A" }}>
            {lang === "es" ? "Usando tu ubicación ahora" : "Using your location now"}
          </span>
        </div>

        {showNearbySection && (
          <section className="mt-5" data-testid="save-spot-nearby-section">
            <div
              style={{
                fontFamily: "'DM Sans', sans-serif", fontSize: 9, color: "#E8B84B",
                letterSpacing: "1.5px", fontWeight: 600, textTransform: "uppercase",
              }}
            >
              {lang === "es" ? "¿ESTÁS EN UNO DE ESTOS LUGARES?" : "ARE YOU AT ONE OF THESE?"}
            </div>
            {tierIdx > 0 && nearby.length > 0 && (
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: "#B0AAA3", marginTop: 4 }}>
                {radiusLabel}
              </div>
            )}
            <ul className="mt-3 space-y-2" data-testid="save-spot-nearby-list">
              {loading ? (
                <li style={{ color: "#B0AAA3", fontFamily: "'DM Sans', sans-serif", fontSize: 12 }}>
                  {lang === "es" ? "Buscando…" : "Searching…"}
                </li>
              ) : nearby.map((b) => (
                <NearbyRow key={b.id} biz={b} saved={savedIds.has(b.id)} onToggle={() => toggleSave(b.id)} />
              ))}
            </ul>
          </section>
        )}

        <div className="my-5 flex items-center" data-testid="save-spot-not-here-divider" style={{ gap: 8, padding: "8px 14px" }}>
          <span style={{ flex: 1, height: 0.5, background: "#222" }} />
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 9, color: "#6a6a6a", letterSpacing: "1px", textTransform: "uppercase" }}>
            {lang === "es" ? "¿NO ESTÁ AQUÍ?" : "NOT HERE?"}
          </span>
          <span style={{ flex: 1, height: 0.5, background: "#222" }} />
        </div>

        <section data-testid="save-spot-custom-section">
          <div style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: 9, color: "#9A958A",
            letterSpacing: "1px", fontWeight: 600, textTransform: "uppercase", marginBottom: 8,
          }}>
            {lang === "es" ? "GUARDAR UN LUGAR PERSONALIZADO" : "SAVE A CUSTOM SPOT"}
          </div>
          <input
            data-testid="save-spot-custom-name"
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder={lang === "es" ? "Nombra este lugar..." : "Name this spot..."}
            style={{
              width: "100%", height: 40,
              background: "#1a1a1a",
              border: "1px solid rgba(232,184,75,0.25)",
              borderRadius: 9, padding: "0 12px",
              color: "#F5F1E8",
              fontFamily: "'DM Sans', sans-serif", fontSize: 12,
              outline: "none",
            }}
          />
          <button
            type="button"
            data-testid="save-spot-custom-save"
            onClick={saveCustom}
            disabled={busy || !customName.trim()}
            className="mt-3 inline-flex items-center justify-center gap-2 w-full transition-opacity hover:opacity-95"
            style={{
              height: 40,
              background: "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
              color: "#0B0B0B", border: "none", borderRadius: 9,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13, fontWeight: 700, letterSpacing: "0.02em",
              opacity: (busy || !customName.trim()) ? 0.5 : 1,
            }}
          >
            <MapPinPlus style={{ width: 14, height: 14 }} strokeWidth={2} />
            {lang === "es" ? "Guardar Esta Ubicación" : "Save This Location"}
          </button>
        </section>
      </div>
    </div>,
    document.body,
  );
}

function NearbyRow({ biz, saved, onToggle }) {
  const distanceText = useMemo(() => {
    const ft = Number(biz.distance_feet) || 0;
    return ft <= 0 ? "<1 ft" : `${ft} ft`;
  }, [biz.distance_feet]);
  return (
    <li
      data-testid={`save-spot-nearby-${biz.id}`}
      className="flex items-center gap-3 py-2"
      style={{ borderBottom: "0.5px solid #1f1f1f" }}
    >
      <span
        className="grid place-items-center shrink-0"
        style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(232,184,75,0.12)", color: "#E8B84B" }}
      >
        <Tag style={{ width: 14, height: 14 }} strokeWidth={1.75} />
      </span>
      <div className="flex-1 min-w-0">
        <div
          className="truncate"
          style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 500, color: "#F5F1E8" }}
        >
          {biz.name}
        </div>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: "#B0AAA3", marginTop: 2 }}>
          {distanceText}
        </div>
      </div>
      <button
        type="button"
        data-testid={`save-spot-heart-${biz.id}`}
        aria-pressed={saved}
        onClick={onToggle}
        className="grid place-items-center transition-colors"
        style={{
          width: 44, height: 44, minWidth: 44, minHeight: 44,
          background: "transparent", border: "none", padding: 0,
        }}
      >
        <span
          aria-hidden="true"
          className="grid place-items-center"
          style={{
            width: 36, height: 36, borderRadius: 9999,
            background: saved ? "rgba(232,184,75,0.15)" : "rgba(0,0,0,0.4)",
            border: saved ? "0.5px solid rgba(232,184,75,0.5)" : "0.5px solid rgba(232,184,75,0.2)",
            color: "#E8B84B",
            transition: "background-color 0.15s ease, border-color 0.15s ease",
          }}
        >
          <Heart style={{ width: 16, height: 16, fill: saved ? "#E8B84B" : "transparent" }} strokeWidth={1.75} />
        </span>
      </button>
    </li>
  );
}

function PulsingDot() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 6, height: 6, borderRadius: 9999,
        background: "#E8B84B",
        boxShadow: "0 0 0 0 rgba(232,184,75,0.55)",
        animation: "habanoPulse 1.4s ease-in-out infinite",
      }}
    />
  );
}
